"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { FileText } from "lucide-react";
import { IconAttach, IconChevronDown, IconClose, IconLink, IconSend, IconSpinner, IconTrash} from "@/components/ui/icons";
import { Select } from "@/components/ui/select";
import { deptColorOf } from "@/lib/dept-colors";
import { api } from "@/lib/api";
import { buildDeptTabTree, type TabNode } from "@/lib/dept-tabs";
import { AttachmentList } from "@/components/team-attachments";
import type { Department, DevRequest, PortalModule } from "@/lib/types";
import { Badge } from "@/components/ui/badge";

/**
 * Вкладка «Доработки» панели командной работы (см. team-panel.tsx):
 * структурированный запрос Отдел → Вкладка → задача (бэклог 01.06). Дерево вкладок
 * переиспользует lib/dept-tabs (та же навигация, что в правах). Только КОНТЕНТ —
 * оболочку/скролл даёт TeamPanel.
 */
export function RequestsTab({ isAdmin = false }: { isAdmin?: boolean }) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [modules, setModules] = useState<PortalModule[]>([]);
  const [deptKey, setDeptKey] = useState("");
  const [topKey, setTopKey] = useState("");
  const [childKey, setChildKey] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mine, setMine] = useState<DevRequest[]>([]);
  const [hoverId, setHoverId] = useState<number | null>(null);
  const [fStatus, setFStatus] = useState("");   // фильтр по статусу ("" = все)
  const [fDept, setFDept] = useState("");        // фильтр по отделу
  const [fPrio, setFPrio] = useState("");        // фильтр по приоритету
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [cFiles, setCFiles] = useState<File[]>([]);   // вложения для нового запроса
  const [cLink, setCLink] = useState("");
  const cFileRef = useRef<HTMLInputElement>(null);
  const cardFileRef = useRef<HTMLInputElement>(null);
  const attachTarget = useRef<number | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [deps, mods] = await Promise.all([api.getDepartments(), api.getModules()]);
        setDepartments(deps);
        setModules(mods);
        if (deps[0]) setDeptKey(deps[0].key);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    void loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadMine() {
    try {
      setMine(await api.listDevRequests(isAdmin ? {} : { mine: true }));
    } catch { /* список не критичен */ }
  }

  const deptModules = useMemo(
    () => modules.filter((m) => (m.departmentKeys ?? []).includes(deptKey)).map((m) => ({ key: m.key, title: m.title })),
    [modules, deptKey],
  );
  const topTabs: TabNode[] = useMemo(
    () => (deptKey ? buildDeptTabTree(deptKey, deptModules) : []),
    [deptKey, deptModules],
  );
  const childTabs: TabNode[] = useMemo(
    () => topTabs.find((t) => t.key === topKey)?.children ?? [],
    [topTabs, topKey],
  );

  function labelOf(deptK: string, topK: string, childK: string): string {
    const dep = departments.find((d) => d.key === deptK)?.title ?? deptK;
    const top = topTabs.find((t) => t.key === topK)?.label ?? "";
    const child = childTabs.find((t) => t.key === childK)?.label ?? "";
    return [dep, top, child].filter(Boolean).join(" → ");
  }

  async function submit() {
    if (!title.trim()) return;
    setSaving(true);
    setError("");
    try {
      const created = await api.createDevRequest({
        dept_key: deptKey, tab_top: topKey, tab_child: childKey,
        tab_label: labelOf(deptKey, topKey, childKey),
        title: title.trim(), description: description.trim(),
        priority: priority as DevRequest["priority"],
      });
      for (const f of cFiles) await api.uploadTeamFile("dev_request", created.id, f);
      if (cLink.trim()) await api.addTeamLink("dev_request", created.id, cLink.trim());
      setTitle("");
      setDescription("");
      setCFiles([]);
      setCLink("");
      await loadMine();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function attachToReq(files: FileList | null) {
    const id = attachTarget.current;
    if (!files || id == null) return;
    try { for (const f of Array.from(files)) await api.uploadTeamFile("dev_request", id, f); await loadMine(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }
  async function addLinkToReq(id: number) {
    const url = window.prompt("Ссылка (https://…)");
    if (!url || !url.trim()) return;
    try { await api.addTeamLink("dev_request", id, url.trim()); await loadMine(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }
  async function delAtt(aid: number) {
    try { await api.deleteTeamAttachment(aid); await loadMine(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  async function changeStatus(id: number, status: string) {
    try { await api.patchDevRequest(id, { status }); await loadMine(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }
  async function remove(id: number) {
    try { await api.deleteDevRequest(id); await loadMine(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  const input =
    "w-full rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surfaceElevated)] px-3 py-2.5 text-[12px] text-[var(--text)] outline-none transition focus:border-[var(--primary)]";
  const STATUS_LABEL: Record<string, string> = { new: "Новая", in_progress: "В работе", done: "Готово", rejected: "Отклонено" };
  const STATUS_COLOR: Record<string, string> = { new: "var(--primary)", in_progress: "#d97706", done: "#16a34a", rejected: "var(--danger)" };
  const PRIO_LABEL: Record<string, string> = { low: "Низкий", normal: "Обычный", high: "Высокий" };
  const PRIO_COLOR: Record<string, string> = { low: "var(--textMuted)", normal: "var(--primary)", high: "var(--danger)" };
  const fmtDate = (iso: string): string => {
    try { return new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  const shown = mine.filter((r) =>
    (!fStatus || r.status === fStatus) && (!fDept || r.dept_key === fDept) && (!fPrio || r.priority === fPrio),
  );
  // отделы, реально встречающиеся в запросах — для компактного фильтра
  const usedDepts = departments.filter((d) => mine.some((r) => r.dept_key === d.key));
  // цвет отдела (палитра синхронна с user-management.tsx / бэком /team/users)
  const deptColor = (key: string): string => {
    const i = departments.findIndex((d) => d.key === key);
    return i >= 0 ? deptColorOf(departments, departments[i].key) : "var(--primary)";
  };

  return (
    <div className="p-4">
      <p className="mb-3 text-[12px] text-[var(--textMuted)]">Выберите отдел и вкладку, опишите, что доработать.</p>
      {error && (
        <p className="mb-3 rounded-[var(--radius-lg)] border border-[var(--danger)] bg-[color-mix(in_srgb,var(--danger)_8%,transparent)] px-3 py-2 text-[12px] text-[var(--danger)]">{error}</p>
      )}

      <div className="mb-3 grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-[var(--text)]">Отдел *</span>
          <Select value={deptKey} onChange={(v) => { setDeptKey(v); setTopKey(""); setChildKey(""); }} className="w-full"
            options={departments.map((d) => ({ value: d.key, label: d.title }))} />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-[var(--text)]">Вкладка</span>
          <Select value={topKey} onChange={(v) => { setTopKey(v); setChildKey(""); }} className="w-full"
            options={[{ value: "", label: "— не выбрано —" }, ...topTabs.map((t) => ({ value: t.key, label: t.label }))]} />
        </label>
      </div>

      {childTabs.length > 0 && (
        <label className="mb-3 block">
          <span className="mb-1 block text-[12px] font-semibold text-[var(--text)]">Подвкладка</span>
          <Select value={childKey} onChange={setChildKey} className="w-full"
            options={[{ value: "", label: "— вся вкладка —" }, ...childTabs.map((t) => ({ value: t.key, label: t.label }))]} />
        </label>
      )}

      <label className="mb-3 block">
        <span className="mb-1 block text-[12px] font-semibold text-[var(--text)]">Что доработать *</span>
        <input className={input} placeholder="Кратко: суть доработки" value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="mb-3 block">
        <span className="mb-1 block text-[12px] font-semibold text-[var(--text)]">Подробности</span>
        <textarea className={`${input} resize-y`} rows={3} placeholder="Что не так сейчас, как должно быть, пример…" value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <label className="mb-3 block">
        <span className="mb-1 block text-[12px] font-semibold text-[var(--text)]">Приоритет</span>
        <Select value={priority} onChange={setPriority} className="w-full"
          options={[{ value: "low", label: "Низкий" }, { value: "normal", label: "Обычный" }, { value: "high", label: "Высокий — мешает работе" }]} />
      </label>

      {/* скриншоты / видео / ссылки — компактно, только значки */}
      <div className="mb-4">
        <input ref={cFileRef} type="file" multiple accept="image/*,video/*" className="hidden"
          onChange={(e) => { const fs = Array.from(e.target.files ?? []); setCFiles((p) => [...p, ...fs]); if (cFileRef.current) cFileRef.current.value = ""; }} />
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => cFileRef.current?.click()} data-tooltip="Прикрепить скриншот / видео"
            className="panel-elevated inline-flex h-9 w-9 shrink-0 items-center justify-center text-[var(--textMuted)] hover:text-[var(--text)]">
            <IconAttach size={15} />
          </button>
          <div className="panel-elevated flex flex-1 items-center gap-1.5 px-2.5">
            <IconLink size={14} className="shrink-0 text-[var(--textMuted)]" />
            <input className="field bg-transparent" placeholder="Ссылка" value={cLink} onChange={(e) => setCLink(e.target.value)} />
          </div>
          <button type="button" onClick={() => void submit()} disabled={saving || !title.trim()} data-tooltip="Отправить"
            className="glass-btn-primary inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--primary)] text-[var(--primaryText)] disabled:opacity-50">
            {saving ? <IconSpinner size={15} className="animate-spin" /> : <IconSend size={15} />}
          </button>
        </div>
        {cFiles.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {cFiles.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-[var(--radius-md)] bg-[var(--surfaceElevated)] px-2 py-1 text-[11px] text-[var(--text)]">
                <FileText size={11} /><span className="max-w-[120px] truncate">{f.name}</span>
                <button type="button" onClick={() => setCFiles((p) => p.filter((_, j) => j !== i))} className="text-[var(--textMuted)] hover:text-[var(--danger)]"><IconClose size={11} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {mine.length > 0 && (
        <div className="mt-5 border-t border-[var(--border)] pt-4">
          <p className="mb-2 text-[12px] font-semibold text-[var(--text)]">
            {isAdmin ? "Все запросы" : "Мои запросы"} <span className="text-[var(--textMuted)]">· {shown.length}</span>
          </p>

          {/* фильтры по статусу (пилюли) */}
          <div className="mb-2 flex flex-wrap gap-1">
            {[{ v: "", l: "Все" }, ...Object.entries(STATUS_LABEL).map(([v, l]) => ({ v, l }))].map((s) => {
              const act = fStatus === s.v;
              const col = s.v ? STATUS_COLOR[s.v] : "var(--primary)";
              return (
                <button key={s.v || "all"} type="button" onClick={() => setFStatus(s.v)}
                  className="rounded-[var(--radius-md)] px-2.5 py-1 text-[11px] font-semibold transition"
                  style={act
                    ? { color: col, background: `color-mix(in srgb, ${col} 16%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${col} 50%, transparent)` }
                    : { color: "var(--textMuted)", background: "var(--surfaceElevated)" }}>
                  {s.l}
                </button>
              );
            })}
          </div>

          {/* фильтры по отделу и приоритету */}
          <div className="mb-3 grid grid-cols-2 gap-2">
            <Select value={fDept} onChange={setFDept} size="sm" className="w-full"
              options={[{ value: "", label: "Все отделы" }, ...usedDepts.map((d) => ({ value: d.key, label: d.title }))]} />
            <Select value={fPrio} onChange={setFPrio} size="sm" className="w-full"
              options={[{ value: "", label: "Любой приоритет" }, ...Object.entries(PRIO_LABEL).map(([value, label]) => ({ value, label }))]} />
          </div>

          {shown.length === 0 ? (
            <p className="py-4 text-center text-[12px] text-[var(--textMuted)]">Ничего не найдено по фильтру</p>
          ) : (
          <ul className="flex flex-col gap-2">
            {shown.map((r) => {
              const rc = deptColor(r.dept_key);
              const on = hoverId === r.id;
              const exp = expandedId === r.id;
              return (
              <li key={r.id}
                onMouseEnter={() => setHoverId(r.id)} onMouseLeave={() => setHoverId(null)}
                className="panel-elevated p-2.5 transition-all"
                style={{
                  borderLeftWidth: 3,
                  borderLeftColor: `color-mix(in srgb, ${rc} ${on ? 90 : 60}%, transparent)`,
                  ...(on ? {
                    borderTopColor: `color-mix(in srgb, ${rc} 45%, var(--border))`,
                    borderRightColor: `color-mix(in srgb, ${rc} 45%, var(--border))`,
                    borderBottomColor: `color-mix(in srgb, ${rc} 45%, var(--border))`,
                    boxShadow: `0 0 14px -3px color-mix(in srgb, ${rc} 65%, transparent)`,
                  } : {}),
                }}>
                <div className="flex items-start justify-between gap-2">
                  <button type="button" onClick={() => setExpandedId(exp ? null : r.id)}
                    className="glass-flat flex min-w-0 flex-1 items-start gap-1.5 text-left">
                    <IconChevronDown size={14} className={`mt-0.5 shrink-0 text-[var(--textMuted)] transition-transform ${exp ? "rotate-180" : ""}`} />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 shrink-0 rounded-full" data-tooltip={`Приоритет: ${PRIO_LABEL[r.priority]}`} style={{ background: PRIO_COLOR[r.priority] }} />
                        <span className="truncate text-[12px] font-semibold text-[var(--text)]">{r.title}</span>
                      </span>
                      {r.tab_label && <span className="block truncate text-[11px] text-[var(--textMuted)]">{r.tab_label}</span>}
                      {isAdmin && r.author_name && <span className="block truncate text-[11px] text-[var(--textMuted)]">👤 {r.author_name}</span>}
                    </span>
                  </button>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {isAdmin ? (
                      <Select value={r.status} onChange={(v) => void changeStatus(r.id, v)} className="w-[120px]"
                        options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))} />
                    ) : (
                      <Badge color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status] ?? r.status}</Badge>
                    )}
                    <button type="button" onClick={() => void remove(r.id)} aria-label="Удалить"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--textMuted)] hover:text-[var(--danger)]">
                      <IconTrash size={14} />
                    </button>
                  </div>
                </div>
                {exp && (
                  <div className="mt-2 space-y-1.5 border-t border-[var(--border)] pt-2 text-[11px]">
                    {r.description
                      ? <p className="whitespace-pre-wrap text-[var(--text)]">{r.description}</p>
                      : <p className="italic text-[var(--textMuted)]">Без описания</p>}
                    <p className="text-[var(--textMuted)]">
                      Приоритет: <span style={{ color: PRIO_COLOR[r.priority] }}>{PRIO_LABEL[r.priority]}</span>
                      {" · "}статус: {STATUS_LABEL[r.status] ?? r.status}
                      {" · "}создано {fmtDate(r.created_at)}
                    </p>
                    {r.attachments && r.attachments.length > 0 && (
                      <AttachmentList items={r.attachments} onDelete={(aid) => void delAtt(aid)} />
                    )}
                    <div className="flex items-center gap-1.5 pt-0.5">
                      <button type="button" onClick={() => { attachTarget.current = r.id; cardFileRef.current?.click(); }} data-tooltip="Прикрепить скриншот / видео"
                        className="panel inline-flex h-7 w-7 items-center justify-center text-[var(--textMuted)] hover:text-[var(--text)]">
                        <IconAttach size={13} />
                      </button>
                      <button type="button" onClick={() => void addLinkToReq(r.id)} data-tooltip="Прикрепить ссылку"
                        className="panel inline-flex h-7 w-7 items-center justify-center text-[var(--textMuted)] hover:text-[var(--text)]">
                        <IconLink size={13} />
                      </button>
                    </div>
                  </div>
                )}
              </li>
              );
            })}
          </ul>
          )}
          <input ref={cardFileRef} type="file" multiple accept="image/*,video/*" className="hidden"
            onChange={(e) => { void attachToReq(e.target.files); if (cardFileRef.current) cardFileRef.current.value = ""; }} />
        </div>
      )}
    </div>
  );
}
